import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import * as T from '../../styles/common/table.styled';
import PageHeader from '../common/PageHeader';
import PageState from '../common/PageState';
import { useParams, useNavigate } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import ContactModal from './ContactModal';
import ConfirmModal from '../common/ConfirmModal';
import toast from 'react-hot-toast';
import { Button } from '../../styles/common/Button.styled';
import { formatPhoneNumber, getCategoryLabel } from '../../utils/format';
import { useAuth } from '../../contexts/AuthContext';

const Container = styled.div`display: flex; flex-direction: column; gap: 24px; max-width: 1120px;`;

const BackButton = styled(Button).attrs({ $variant: "ghost", $size: "sm" })``;



const SectionTitle = styled.h2`font-size: 14px; font-weight: 600; color: ${({ theme }) => theme.color.text_color_100}; margin: 0 0 16px;`;

const InfoGrid = styled.div`display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 24px; padding: 20px 0; border-top: 1px solid ${({ theme }) => theme.color.primary_300};`;

const InfoItem = styled.div`display: flex; flex-direction: column; gap: 8px; min-width: 0;`;

const InfoLabel = styled.span`font-size: 12px; color: ${({ theme }) => theme.color.text_color_300};`;

const InfoValue = styled.div`font-size: 13px; color: ${({ theme }) => theme.color.text_color_100}; line-height: 1.8; white-space: pre-wrap; overflow-wrap: anywhere;`;

const PhoneList = styled.div`display: flex; flex-direction: column; gap: 8px;`;

const PhoneCard = styled.div`display: flex; align-items: center; gap: 12px; min-height: 36px; font-size: 15px; font-variant-numeric: tabular-nums;`;



const PrimaryLabel = styled(T.Badge).attrs({ $tone: "info" })``;







const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: contactInfo, loading, error, request } = useApi();
  const { request: deleteRequest } = useApi();
  const { mode } = useAuth();
  const isCoreMode = mode === 'core';

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const fetchContactDetail = useCallback(async () => {
    if (!id) return;
    await request({
      url: `/contact/${id}`,
      method: 'GET'
    });
  }, [id, request]);

  useEffect(() => {
    fetchContactDetail();
  }, [fetchContactDetail, mode]);

  const handleDelete = async () => {
    try {
      await deleteRequest({
        url: `/contact/${id}`,
        method: 'DELETE'
      });
      toast.success('주소록이 삭제되었습니다.');
      navigate('/contact');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '삭제 중 오류가 발생했습니다.';
      toast.error(errorMessage);
    }
  };

  if (loading && !contactInfo) return <PageState status="loading" />;
  if (error) return <PageState status="error" onRetry={fetchContactDetail} />;
  if (!contactInfo) return <PageState status="empty" title="연락처를 찾을 수 없습니다." />;
  const phones = [...(contactInfo.phones?.length ? contactInfo.phones : [{ phone: contactInfo.phone, isPrimary: true }])]
    .sort((a: any, b: any) => Number(b.isPrimary) - Number(a.isPrimary));
  return <Container>
    <PageHeader title={contactInfo.name || '연락처 상세'} description="연락처 정보" actions={<>
      <BackButton onClick={() => navigate('/contact')}>← 목록으로 돌아가기</BackButton>
      {!isCoreMode && <><Button $variant="secondary" onClick={() => setIsEditModalOpen(true)}>수정</Button><Button $variant="danger" onClick={() => setIsDeleteModalOpen(true)}>삭제</Button></>}
    </>} />
    <section><SectionTitle>고객 기본 정보</SectionTitle><InfoGrid>
      <InfoItem style={{ gridColumn: '1 / -1' }}><InfoLabel>전화번호</InfoLabel>
        {isCoreMode ? <InfoValue>{formatPhoneNumber(contactInfo.phone)}</InfoValue> : <PhoneList>{phones.map((phone: any, index: number) => <PhoneCard key={index}>
          <span>{formatPhoneNumber(phone.phone)}</span>{phone.isPrimary && <PrimaryLabel>대표번호</PrimaryLabel>}
        </PhoneCard>)}</PhoneList>}
      </InfoItem>
      {!isCoreMode && <>
        <InfoItem><InfoLabel>카테고리</InfoLabel><InfoValue>{getCategoryLabel(contactInfo.category)}</InfoValue></InfoItem>
        <InfoItem><InfoLabel>등록일</InfoLabel><InfoValue>{contactInfo.createdAt ? new Date(contactInfo.createdAt).toLocaleString() : '-'}</InfoValue></InfoItem>
        <InfoItem style={{ gridColumn: '1 / -1' }}><InfoLabel>메모</InfoLabel><InfoValue>{contactInfo.memo || '-'}</InfoValue></InfoItem>
      </>}
    </InfoGrid></section>
    {!isCoreMode && contactInfo.extensionFields?.map((section: any) => <section key={section.sectionName}>
      <SectionTitle>{section.sectionName}</SectionTitle><InfoGrid>{section.fields.map((field: any) => <InfoItem key={field.key}><InfoLabel>{field.displayName}</InfoLabel><InfoValue>{field.value || '-'}</InfoValue></InfoItem>)}</InfoGrid>
    </section>)}
    {!isCoreMode && <ContactModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSuccess={fetchContactDetail} mode="update" initialData={contactInfo} />}
    {!isCoreMode && isDeleteModalOpen && <ConfirmModal title="주소록 삭제" message={`'${contactInfo.name}'님의 연락처 (${phones.map((p: any) => formatPhoneNumber(p.phone)).join(', ')})를 삭제하시겠습니까?\n삭제된 데이터는 복구할 수 없습니다.`} confirmLabel="삭제" onConfirm={handleDelete} onCancel={() => setIsDeleteModalOpen(false)} />}
  </Container>;
};
export default ContactDetail;
